"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/src/lib/api-fetch";
import {
  AlertTriangle,
  Braces,
  CheckCircle2,
  Database,
  FileText,
  GraduationCap,
  Info,
  Languages,
  ListChecks,
  Loader2,
  MessageSquareText,
  RefreshCw,
  RotateCcw,
  Sparkles,
  X,
  type LucideIcon,
} from "lucide-react";
import type { CatalogModel } from "@/lib/llm-models/catalog";
import { TASK_CATEGORIES, type TaskSetting } from "@/lib/llm-models/task-settings";
import {
  lacksJsonSupportForTask,
  MAX_OUTPUT_TOKENS_LIMIT,
  outputTokenCeiling,
  worstCaseCostUsd,
} from "@/lib/llm-models/rules";
import ModelPicker from "./ModelPicker";
import EffortPicker from "./EffortPicker";
import { effortLabel, formatDateTime, perMillion, usd } from "./format";

type ApiResponse = {
  tableMissing: boolean;
  tasks: TaskSetting[];
  catalog: { models: CatalogModel[]; fetchedAt: number; stale: boolean } | null;
  catalogError: string | null;
  /** Warn when one call's worst case is above this (OPENROUTER_WORST_CASE_WARN_USD). */
  warnUsd: number;
  /** Providers we have our own key for in OpenRouter (OPENROUTER_BYOK_PROVIDERS). */
  providers: string[];
};

const PROVIDER_LABELS: Record<string, string> = { openai: "OpenAI", anthropic: "Anthropic (Claude)", google: "Google" };

function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

/** `maxTokens` is the raw input text, so the field can be cleared while typing. */
type Draft = { model: string; effort: string | null; maxTokens: string };
type Toast = { message: string; type: "success" | "error" } | null;

/** Saved rows may hold a bare OpenAI id ("gpt-6-luna"); the catalog uses "openai/gpt-6-luna". */
function catalogId(model: string): string {
  return model.includes("/") ? model : `openai/${model}`;
}

/** The effort to land on after switching models: keep the old one if the new model has it. */
function pickEffort(model: CatalogModel | undefined, current: string | null): string | null {
  if (!model?.supportsReasoning) return null;
  if (current && model.efforts.includes(current as CatalogModel["efforts"][number])) return current;
  if (model.defaultEffort && model.efforts.includes(model.defaultEffort)) return model.defaultEffort;
  if (model.efforts.includes("medium")) return "medium";
  return model.efforts[0] ?? null;
}

const TASK_ICONS: Record<string, LucideIcon> = {
  course_outline_generation: GraduationCap,
  canonical_lesson: FileText,
  personalized_scenario: MessageSquareText,
  personalized_scenario_translated: Languages,
  remedial_lesson: RotateCcw,
  generate_quiz: ListChecks,
  generate_placement_quiz: ListChecks,
  translate_lesson: Languages,
  translate_quiz: Languages,
  translate_placement_quiz: Languages,
};

/** The output cap a draft holds, or null when the text is not a whole number. */
function parseTokens(text: string): number | null {
  if (!/^\d+$/.test(text.trim())) return null;
  return Number(text.trim());
}

function Warning({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg bg-amber-50/70 px-3 py-2.5 text-xs leading-relaxed text-amber-900 ring-1 ring-inset ring-amber-200">
      <Icon className="mt-px h-4 w-4 shrink-0 text-amber-500" />
      <p>
        <span className="font-semibold">{title}</span> {children}
      </p>
    </div>
  );
}

function Stat({ label, value, tone = "default" }: { label: string; value: string; tone?: "default" | "warn" }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs ring-1 ring-inset ${
        tone === "warn" ? "bg-amber-50 text-amber-700 ring-amber-200" : "bg-slate-50 text-slate-500 ring-slate-200"
      }`}
    >
      {label}
      <span className={`font-semibold tabular-nums ${tone === "warn" ? "text-amber-800" : "text-slate-800"}`}>{value}</span>
    </span>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <span className="sr-only">Loading model settings…</span>
      <div className="animate-pulse rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="h-4 w-32 rounded bg-slate-200" />
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-16 rounded-lg bg-slate-100" />
          ))}
        </div>
      </div>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="animate-pulse rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-slate-100" />
            <div className="space-y-2">
              <div className="h-4 w-40 rounded bg-slate-200" />
              <div className="h-3 w-64 rounded bg-slate-100" />
            </div>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <div className="h-10 rounded-lg bg-slate-100" />
            <div className="h-10 rounded-lg bg-slate-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AiModelsClient() {
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saving, setSaving] = useState<ReadonlySet<string>>(new Set());
  const [toast, setToast] = useState<Toast>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await apiFetch("/api/admin/ai-models");
      if (!res.ok) throw new Error(res.status === 403 ? "Only inaraX admins can manage AI models." : "Failed to load model settings.");
      setData((await res.json()) as ApiResponse);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "Failed to load model settings.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const models = useMemo(() => data?.catalog?.models ?? [], [data]);
  const byId = useMemo(() => new Map(models.map((m) => [m.id, m])), [models]);

  const save = async (task: TaskSetting, draft: Draft) => {
    setSaving((prev) => new Set(prev).add(task.task));
    try {
      const res = await apiFetch(`/api/admin/ai-models/${encodeURIComponent(task.task)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: draft.model, reasoningEffort: draft.effort, maxOutputTokens: parseTokens(draft.maxTokens) }),
      });
      if (res.status === 401) throw new Error("Your session has expired. Sign in again to save.");
      if (res.status === 403) throw new Error("Only inaraX admins can change AI models.");
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "Failed to save");
      setData((prev) =>
        prev
          ? {
              ...prev,
              tasks: prev.tasks.map((t) =>
                t.task === task.task
                  ? {
                      ...t,
                      model: body.model,
                      isReasoning: body.isReasoning,
                      reasoningEffort: body.reasoningEffort,
                      maxOutputTokens: body.maxOutputTokens,
                      updatedAt: body.updatedAt,
                      updatedBy: body.updatedBy,
                    }
                  : t,
              ),
            }
          : prev,
      );
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[task.task];
        return next;
      });
      setToast({ message: `${task.label} now uses ${byId.get(body.model)?.name ?? body.model}. Workers pick it up within a minute.`, type: "success" });
    } catch (e) {
      setToast({ message: e instanceof Error ? e.message : "Failed to save", type: "error" });
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(task.task);
        return next;
      });
    }
  };

  if (loadError) {
    return (
      <div className="rounded-xl bg-white p-8 text-center shadow-sm ring-1 ring-slate-200">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <h2 className="mt-3 text-base font-semibold text-slate-900">Could not load model settings</h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{loadError}</p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Try again
        </button>
      </div>
    );
  }

  if (!data) return <LoadingSkeleton />;

  if (data.tableMissing || data.tasks.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500">
          <Database className="h-5 w-5" />
        </div>
        <h2 className="mt-3 text-base font-semibold text-slate-900">No model settings yet</h2>
        <p className="mx-auto mt-1 max-w-lg text-sm text-slate-500">
          The generation tasks appear here once the inara migration that creates the{" "}
          <code className="rounded bg-slate-100 px-1 py-0.5 text-xs text-slate-700">llm_task_settings</code> table has run on this
          database. Until then the workers use their built-in defaults.
        </p>
      </div>
    );
  }

  const canSave = !!data.catalog && !data.catalogError;

  return (
    <div className="space-y-5">
      {/* Overview: what each task runs on right now (saved values). */}
      <section className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-5 pt-4">
          <h2 className="text-sm font-semibold text-slate-900">Current setup</h2>
          {data.catalog ? (
            <p className="text-xs text-slate-500">
              {data.catalog.models.length} models available · prices updated {formatDateTime(data.catalog.fetchedAt)}
              {data.catalog.stale ? <span className="ml-1 font-medium text-amber-700">(could not refresh, showing the last copy)</span> : null}
            </p>
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-1.5 p-4 sm:grid-cols-2 sm:gap-2 lg:grid-cols-3">
          {data.tasks.map((task) => {
            const savedModel = byId.get(catalogId(task.model));
            const Icon = TASK_ICONS[task.task] ?? Sparkles;
            const hasDraft = !!drafts[task.task];
            return (
              <a
                key={task.task}
                href={`#task-${task.task}`}
                className="group flex items-center gap-3 rounded-lg px-3 py-2 ring-1 sm:py-2.5 ring-inset ring-slate-100 transition hover:bg-slate-50 hover:ring-slate-200"
              >
                <Icon className="hidden h-4 w-4 shrink-0 text-slate-400 group-hover:text-indigo-500 sm:block" />
                <div className="flex min-w-0 flex-1 items-baseline justify-between gap-3 sm:block">
                  <p className="truncate text-xs text-slate-500">{task.label}</p>
                  <p className="shrink-0 truncate text-sm font-semibold text-slate-900 sm:shrink">
                    {savedModel ? savedModel.name : task.model}
                    {task.isReasoning && task.reasoningEffort ? (
                      <span className="font-normal text-slate-500"> · {effortLabel(task.reasoningEffort)}</span>
                    ) : null}
                  </p>
                </div>
                {hasDraft ? <span className="h-2 w-2 shrink-0 rounded-full bg-amber-400" title="Unsaved changes" /> : null}
              </a>
            );
          })}
        </div>
        <div className="flex items-start gap-2 border-t border-slate-100 px-5 py-3 text-xs leading-relaxed text-slate-500">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-indigo-500" />
          <p>
            Tasks run through OpenRouter on our own provider keys. Only models from providers we have a key for are listed
            (now: {data.providers.map(providerLabel).join(", ")}). A saved change reaches the workers within about a minute.
          </p>
        </div>
      </section>

      {data.catalogError ? (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">{data.catalogError}</span>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-sm font-medium text-red-700 shadow-sm ring-1 ring-red-200 hover:bg-red-50"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Retry
          </button>
        </div>
      ) : null}

      {TASK_CATEGORIES.map((category) => {
        const categoryTasks = data.tasks.filter((t) => (t.category || "curriculum") === category.id);
        if (categoryTasks.length === 0) return null;

        return (
          <div key={category.id} className="space-y-4 pt-6 first:pt-2">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-2.5">
              <div>
                <h2 className="text-lg font-bold tracking-tight text-slate-900">{category.title}</h2>
                <p className="mt-0.5 text-xs text-slate-500">{category.description}</p>
              </div>
              <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-200">
                {categoryTasks.length} {categoryTasks.length === 1 ? "task" : "tasks"}
              </span>
            </div>

            <div className="space-y-4">
              {categoryTasks.map((task) => {
        const draft = drafts[task.task] ?? {
          model: catalogId(task.model),
          effort: task.reasoningEffort,
          maxTokens: String(task.maxOutputTokens),
        };
        const selected = byId.get(draft.model);
        const savedId = catalogId(task.model);
        const savedInCatalog = byId.has(savedId);
        const tokens = parseTokens(draft.maxTokens);
        const ceiling = selected ? outputTokenCeiling(selected) : MAX_OUTPUT_TOKENS_LIMIT;
        const tokensValid = tokens !== null && tokens >= 1 && tokens <= ceiling;
        // Without the model in the catalog there is nothing to say whether it reasons, so compare the effort as stored.
        const draftEffort = selected ? (selected.supportsReasoning ? draft.effort : null) : draft.effort;
        const savedEffort = selected ? (task.isReasoning ? task.reasoningEffort : null) : task.reasoningEffort;
        const dirty = draft.model !== savedId || draftEffort !== savedEffort || tokens !== task.maxOutputTokens;
        const effortMissing = !!selected?.supportsReasoning && !draft.effort;
        const worstCase = selected && tokens !== null ? worstCaseCostUsd(tokens, selected) : null;
        const risky = worstCase !== null && worstCase > data.warnUsd;
        const weakJson = selected ? lacksJsonSupportForTask(task.task, selected) : false;
        const setDraft = (patch: Partial<Draft>) => setDrafts((prev) => ({ ...prev, [task.task]: { ...draft, ...patch } }));
        const isSaving = saving.has(task.task);
        const Icon = TASK_ICONS[task.task] ?? Sparkles;
        const pickersDisabled = !canSave || isSaving;

        return (
          <section
            key={task.task}
            id={`task-${task.task}`}
            className={`scroll-mt-36 rounded-xl bg-white shadow-sm ring-1 transition ${dirty ? "ring-indigo-300" : "ring-slate-200"}`}
          >
            <div className="p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <h2 className="text-base font-semibold text-slate-900">{task.label}</h2>
                    {dirty ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Unsaved changes
                      </span>
                    ) : null}
                  </div>
                  {task.description ? <p className="mt-0.5 max-w-2xl text-sm text-slate-500">{task.description}</p> : null}
                </div>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-[minmax(0,3fr)_minmax(0,1.6fr)_minmax(0,1.2fr)]">
                <div>
                  <label htmlFor={`model-${task.task}`} className="mb-1.5 block text-xs font-semibold text-slate-600">
                    Model
                  </label>
                  <ModelPicker
                    id={`model-${task.task}`}
                    testId={`model-picker-${task.task}`}
                    value={draft.model}
                    models={models}
                    byId={byId}
                    unlisted={!savedInCatalog ? { value: savedId, label: task.model } : null}
                    disabled={pickersDisabled}
                    onChange={(modelId) => {
                      const next = byId.get(modelId);
                      // A model with a lower output ceiling caps the draft at that ceiling.
                      const nextCeiling = next ? outputTokenCeiling(next) : MAX_OUTPUT_TOKENS_LIMIT;
                      const capped = tokens !== null && tokens > nextCeiling ? String(nextCeiling) : draft.maxTokens;
                      setDraft({ model: modelId, effort: pickEffort(next, draft.effort), maxTokens: capped });
                    }}
                  />
                  {!selected && !savedInCatalog && draft.model === savedId && canSave ? (
                    <p className="mt-1.5 flex items-center gap-1 text-xs text-amber-700">
                      <AlertTriangle className="h-3 w-3" /> The saved model is not in the OpenRouter list. Pick one from the list to save.
                    </p>
                  ) : null}
                </div>

                <div>
                  {selected?.supportsReasoning ? (
                    <>
                      <label htmlFor={`effort-${task.task}`} className="mb-1.5 block text-xs font-semibold text-slate-600">
                        Reasoning effort
                      </label>
                      <EffortPicker
                        id={`effort-${task.task}`}
                        testId={`effort-picker-${task.task}`}
                        value={draft.effort}
                        model={selected}
                        disabled={pickersDisabled}
                        onChange={(effort) => setDraft({ effort })}
                      />
                    </>
                  ) : (
                    <>
                      <span className="mb-1.5 block text-xs font-semibold text-slate-600">Reasoning effort</span>
                      <div className="flex h-11 items-center rounded-lg border border-dashed border-slate-300 px-3 text-sm text-slate-400">
                        {selected ? "This model does not reason" : "Not available"}
                      </div>
                    </>
                  )}
                </div>

                <div>
                  <label htmlFor={`tokens-${task.task}`} className="mb-1.5 block text-xs font-semibold text-slate-600">
                    Max output tokens
                  </label>
                  <input
                    id={`tokens-${task.task}`}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={ceiling}
                    step={1}
                    value={draft.maxTokens}
                    disabled={pickersDisabled}
                    onChange={(e) => setDraft({ maxTokens: e.target.value })}
                    aria-invalid={!tokensValid}
                    aria-describedby={`tokens-help-${task.task}`}
                    className={`h-11 w-full rounded-lg border bg-white px-3 text-sm font-medium tabular-nums text-slate-900 shadow-sm focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 ${
                      tokensValid ? "border-slate-300 focus:border-indigo-500 focus:ring-indigo-500/20" : "border-red-400 focus:border-red-500 focus:ring-red-500/20"
                    }`}
                  />
                  <p id={`tokens-help-${task.task}`} className={`mt-1.5 text-xs ${tokensValid ? "text-slate-500" : "text-red-600"}`}>
                    {tokensValid
                      ? selected?.maxCompletionTokens && selected.maxCompletionTokens < MAX_OUTPUT_TOKENS_LIMIT
                        ? `Up to ${selected.maxCompletionTokens.toLocaleString()}, this model's limit.`
                        : `Up to ${MAX_OUTPUT_TOKENS_LIMIT.toLocaleString()}.`
                      : `Enter a whole number from 1 to ${ceiling.toLocaleString()}${
                          selected?.maxCompletionTokens && ceiling === selected.maxCompletionTokens ? ", this model's limit" : ""
                        }.`}
                  </p>
                </div>
              </div>

              {selected ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  <Stat label="Price per 1M" value={`${perMillion(selected.inputPerMillion)} in · ${perMillion(selected.outputPerMillion)} out`} />
                  <Stat label="Max output cost per call" value={worstCase === null ? "-" : usd(worstCase)} tone={risky ? "warn" : "default"} />
                </div>
              ) : null}

              {selected && (weakJson || risky) ? (
                <div className="mt-3 space-y-2">
                  {weakJson ? (
                    <Warning icon={Braces} title="No JSON output support.">
                      This task&apos;s replies are checked as JSON, and this model supports neither a strict JSON schema nor JSON mode, so
                      more of its replies are likely to fail that check.
                    </Warning>
                  ) : null}
                  {risky ? (
                    <Warning icon={AlertTriangle} title="Expensive worst case.">
                      One call could cost up to {usd(worstCase ?? 0)} in output, above the {usd(data.warnUsd)} warning threshold.
                    </Warning>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 rounded-b-xl border-t border-slate-100 bg-slate-50/60 px-5 py-3">
              <p className="text-xs text-slate-500">
                Last changed {task.updatedAt ? formatDateTime(task.updatedAt) : "never"}
                {task.updatedBy ? ` by ${task.updatedBy}` : ""}
              </p>
              <div className="ml-auto flex items-center gap-2">
                {dirty ? (
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={() =>
                      setDrafts((prev) => {
                        const next = { ...prev };
                        delete next[task.task];
                        return next;
                      })
                    }
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200/60 disabled:opacity-50"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Reset
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={!dirty || !canSave || isSaving || !selected || !tokensValid || effortMissing}
                  onClick={() => void save(task, draft)}
                  className="inline-flex min-w-[5.5rem] items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {isSaving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
                  </section>
                );
              })}
            </div>
          </div>
        );
      })}

      {toast ? (
        <div
          role="status"
          className="fixed inset-x-4 bottom-4 z-50 flex items-start gap-3 rounded-xl bg-white px-4 py-3 text-sm text-slate-700 shadow-lg shadow-slate-900/10 ring-1 ring-slate-200 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:max-w-sm"
        >
          {toast.type === "success" ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          ) : (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
          )}
          <span className="flex-1">{toast.message}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Dismiss" className="-mr-1 rounded p-0.5 text-slate-400 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
